import { useContext, useEffect, useState } from 'react';
import { Box, Page } from '@zillow/constellation-10';
import BlockedUserModal from '@zg-rentals/blocked-user-modal';
import { CustomizedBlockedUserModal } from '@zg-rentals/satellite-components';
import type { SystemStyleObject } from '@/styled-system/types';
import { PageHeaderAsync } from '@zillow/page-frame';
import { styledComponentSet } from '@zillow/page-frame/styledComponentSet';
import { AppContext } from '../../contexts/AppContextProvider';
import HotpadsHeaderNav from '../../components/HotpadsHeaderNav';
import type { User } from '../../AuthProvider';
import useBooleanConfig from '../../hooks/useBooleanConfig';
import { isHotpads, isZillow } from '@zg-rentals/multibrand-support';
import getPandaThemeAttributes from '../../utils/getPandaThemeAttributes';

type Props = WithChildren<{
  user?: User;
  showHeader?: boolean;
  pageWrapperStyles?: SystemStyleObject;
  mainContentStyles?: SystemStyleObject;
}>;

export default function Layout({
  children,
  user,
  showHeader = true,
  pageWrapperStyles = {},
  mainContentStyles = {},
}: Props) {
  const { brand } = useContext(AppContext);
  const canAccessBlockedUserModalFromRJSModule = useBooleanConfig('canAccessBlockedUserModalFromRJSModule', false);
  const { email, firstName, isLoggedIn, isUserBlocked, restrictions } = user || {};
  const isAllowed = restrictions?.isAllowed || false;
  const isHotpadsBrand = isHotpads(brand);
  const [shouldDisplayCustomizedBlockedUserModal, setShouldDisplayCustomizedBlockedUserModal] = useState(false);
  const [shouldDisplayBlockedUserModalFromModule, setShouldDisplayBlockedUserModalFromModule] = useState(false);

  useEffect(() => {
    if (isUserBlocked) {
      if (canAccessBlockedUserModalFromRJSModule && !isAllowed) {
        setShouldDisplayCustomizedBlockedUserModal(false);
        setShouldDisplayBlockedUserModalFromModule(true);
      } else if (!canAccessBlockedUserModalFromRJSModule) {
        setShouldDisplayCustomizedBlockedUserModal(true);
        setShouldDisplayBlockedUserModalFromModule(false);
      }
    }
  }, [canAccessBlockedUserModalFromRJSModule, isUserBlocked, isAllowed]);

  // Set HTML tag attributes for Panda theme
  useEffect(() => {
    if (brand) {
      const attributes = getPandaThemeAttributes(brand);
      const htmlElement = document.documentElement;
      Object.entries(attributes).forEach(([key, value]) => {
        if (value) {
          htmlElement.setAttribute(key, value);
        } else {
          htmlElement.removeAttribute(key);
        }
      });
    }
  }, [brand]);

  return (
    <Box css={{ height: '100%', ...pageWrapperStyles }}>
      {/* use the blocked user modal from the blocked-user-modal module, default to use our version if the feature flag value is false */}
      {shouldDisplayCustomizedBlockedUserModal && <CustomizedBlockedUserModal brand={brand} />}
      {shouldDisplayBlockedUserModalFromModule && user != null && <BlockedUserModal user={user} />}

      {/* TODO: Replace @zillow/page-frame with @zillow/page-frame-next instead to render the zillow header nav */}
      {showHeader && isZillow(brand) && (
        <PageHeaderAsync mainAsync componentSet={styledComponentSet} gaEventHandler={() => undefined} />
      )}
      {showHeader && isHotpadsBrand && (
        <HotpadsHeaderNav email={email || ''} loggedIn={isLoggedIn || false} firstName={firstName || ''} />
      )}
      <Page.Root
        asChild
        css={{
          backgroundColor: {
            base: 'bg.screen.softest',
            _hotpadsTheme: 'transparent',
          },
          paddingBottom: '0',
          height: '100%',
          ...mainContentStyles,
        }}
      >
        <main>{children}</main>
      </Page.Root>
    </Box>
  );
}
